import { groupBy } from 'lodash'
import type { DocumentFragment } from '~app/graph/generated/graphql'
import { renderScanId } from '../../components/DocumentDialog/utils'
import DocumentTable from '../../components/Elements/DocumentTable'
import DocumentTableDivider from '../../components/Elements/DocumentTableDivider'
import DocumentTableLegend from '../../components/Elements/DocumentTableLegend'
import DocumentTableRow from '../../components/Elements/DocumentTableRow'
import { NO_CUSTOMER } from './constants'

interface Props {
  documents: DocumentFragment[]
  openedDocumentIds: Set<string>
  hasTagger: boolean
  onSelectDocument: (id: string) => void
  onDone: () => void
}

const DocumentList = ({
  documents,
  openedDocumentIds,
  hasTagger,
  onSelectDocument,
  onDone,
}: Props) => {
  const documentsWithCustomer = documents.map(document => {
    if (document.customer) {
      return document
    }
    return { ...document, customer: NO_CUSTOMER } satisfies DocumentFragment
  })

  const documentsByCustomerId = groupBy(
    documentsWithCustomer,
    item => item.customer?.id
  )

  return (
    <DocumentTable>
      {Object.keys(documentsByCustomerId).reduce<React.ReactNode[]>(
        ($elements, customerId) => {
          const groupedDocuments = documentsByCustomerId[customerId]
          const [document] = groupedDocuments
          const customer = document.customer ?? NO_CUSTOMER
          const name = customer.companyName
            ? customer.companyName
            : `${customer.firstName} ${customer.lastName}`

          $elements.push(
            <DocumentTableDivider
              key={customer.id}
              id={customer.id}
              publicId={customer.encodedPublicId}
              name={name}
              count={groupedDocuments.length}
            />
          )

          $elements.push(<DocumentTableLegend key={customer.encodedPublicId} />)

          for (const item of groupedDocuments) {
            $elements.push(
              <DocumentTableRow
                key={item.id}
                id={item.id}
                stage={item.stage}
                tags={item.tags}
                rules={item.rules || []}
                scanId={renderScanId(item)}
                scanCenter={item.scanCenter || undefined}
                from={item.senderMetadata?.name || ''}
                subject={item.subject || ''}
                date={item.createdAt}
                onClick={() => onSelectDocument(item.id)}
                onDone={onDone}
                isOpened={openedDocumentIds.has(item.id)}
                paymentMetadata={item.paymentMetadata}
                showActions={hasTagger}
              />
            )
          }
          return $elements
        },
        []
      )}
    </DocumentTable>
  )
}

export default DocumentList
